import React, { ReactNode } from 'react';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { buildLoginUrl, buildStaffLoginUrl, getReturnPathForAuthRedirect } from '../utils/authReturnUrl';
import {
  clearPersistedAuth,
  persistAuthRaw,
  readPersistedAuthRaw,
} from '../utils/authStorage';
import { authAudienceHeaders, getAuthAudience } from '../utils/authAudience';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'http://localhost:3001';

const CUSTOMER_IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const STAFF_IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const LAST_ACTIVITY_KEY = 'lewouhmart_last_activity_at';

type AuthState = {
  accessToken: string | null;
  refreshToken: string | null;
  mfaRequired: boolean;
  challengeId: string | null;
  email: string;
  type: string | null;
  requiresEmailVerification?: boolean;
  permissions?: string[] | null;
};

type RegisterPayload = {
  email: string;
  password: string;
  fullName: string;
  phone: string;
  country: string;
  countryCode?: string;
  city: string;
  street: string;
  street2?: string;
  stateOrProvince?: string;
  postalCode: string;
  latitude?: number;
  longitude?: number;
  addressSource?: 'MANUAL' | 'SUGGESTION';
  referralCode?: string;
  referralDeviceId?: string;
};

type LoginPayload = {
  email: string;
  password: string;
};

type AuthContextValue = {
  auth: AuthState;
  isAuthenticated: boolean;
  register: (payload: RegisterPayload) => Promise<unknown>;
  login: (payload: LoginPayload) => Promise<{
    mfaRequired: boolean;
    requiresEmailVerification?: boolean;
  }>;
  completeMfa: (code: string) => Promise<void>;
  resendMfaCode: () => Promise<void>;
  /** Clears MFA / partial-login state in localStorage and returns UI to the sign-in form. */
  cancelMfaFlow: () => void;
  /**
   * Passwordless shipping-agent sign-in step 1: emails a 6-digit code and
   * stores `mfaRequired + challengeId + email` in auth state. Step 2 is the
   * existing `completeMfa(code)` method.
   */
  requestAgentLoginCode: (email: string) => Promise<void>;
  logout: () => void;
  authorizedFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  refreshPermissions: () => Promise<void>;
  fetchPreferences: () => Promise<{
    language?: string;
    currency?: string;
    fontSizePreset?: 'sm' | 'md' | 'lg' | 'xl';
    useGlobalFontSize?: boolean;
  } | null>;
  updatePreferences: (partial: {
    language?: string;
    currency?: string;
    fontSizePreset?: 'sm' | 'md' | 'lg' | 'xl';
    useGlobalFontSize?: boolean;
  }) => Promise<void>;
  loginWithPasskey: (email: string) => Promise<void>;
  registerCurrentDevicePasskey: (nickname?: string) => Promise<void>;
  regenerateRecoveryCodes: (currentPassword: string) => Promise<string[]>;
  recoverAccountWithCode: (payload: {
    email: string;
    code: string;
    newEmail?: string;
    newPassword: string;
  }) => Promise<void>;
  listPasskeys: () => Promise<Array<{
    id: string;
    nickname?: string | null;
    createdAt: string;
    lastUsedAt?: string | null;
    deviceType?: string | null;
    backedUp?: boolean | null;
  }>>;
  renamePasskey: (id: string, nickname?: string) => Promise<void>;
  deletePasskey: (id: string) => Promise<void>;
  consumeAuthResult: (payload: {
    accessToken: string;
    refreshToken?: string | null;
    type?: string | null;
    email?: string | null;
  }) => Promise<void>;
};

const defaultAuthState: AuthState = {
  accessToken: null,
  refreshToken: null,
  mfaRequired: false,
  challengeId: null,
  email: '',
  type: null,
  permissions: null,
};

const AuthContext = React.createContext<AuthContextValue>({
  auth: defaultAuthState,
  isAuthenticated: false,
  register: async () => ({}),
  login: async () => ({ mfaRequired: false }),
  completeMfa: async () => {},
  resendMfaCode: async () => {},
  cancelMfaFlow: () => {},
  requestAgentLoginCode: async () => {},
  logout: () => {},
   // Fallback implementation; real one provided in AuthProvider
  authorizedFetch: async (input: RequestInfo | URL, init?: RequestInit) => fetch(input, init),
  refreshPermissions: async () => {},
  fetchPreferences: async () => null,
  updatePreferences: async () => {},
  loginWithPasskey: async () => {},
  registerCurrentDevicePasskey: async () => {},
  regenerateRecoveryCodes: async () => [],
  recoverAccountWithCode: async () => {},
  listPasskeys: async () => [],
  renamePasskey: async () => {},
  deletePasskey: async () => {},
  consumeAuthResult: async () => {},
});

type AuthProviderProps = {
  children: ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [auth, setAuth] = React.useState<AuthState>(() => {
    const raw = readPersistedAuthRaw();
    if (!raw) return defaultAuthState;
    try {
      return { ...defaultAuthState, ...JSON.parse(raw) };
    } catch {
      return defaultAuthState;
    }
  });

  const isAuthenticated = Boolean(auth.accessToken);

  const touchActivity = React.useCallback(() => {
    try {
      window.localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    } catch {
      // ignore
    }
  }, []);

  const persistAuth = React.useCallback((next: AuthState) => {
    setAuth(next);
    persistAuthRaw(JSON.stringify(next));
  }, []);

  const register = React.useCallback(async (payload: RegisterPayload) => {
    const res = await fetch(`${API_BASE}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const raw = (err as { message?: string | string[] }).message;
      const message = Array.isArray(raw)
        ? raw[0]
        : (typeof raw === 'string' ? raw : null);
      throw new Error(message || 'Unable to create account.');
    }

    return res.json();
  }, []);

  const fetchPermissions = React.useCallback(async (token: string | null, userType?: string | null) => {
    if (!token) return null;
    if (userType === 'CUSTOMER') return null;
    const res = await fetch(`${API_BASE}/rbac/me/permissions`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!res.ok) return null;
    return res.json() as Promise<string[]>;
  }, []);

  const login = React.useCallback(async ({ email, password }: LoginPayload) => {
    const audience = getAuthAudience();
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authAudienceHeaders(),
      },
      body: JSON.stringify({ email, password, audience }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const message = (err as { message?: string }).message
        || 'Unable to sign in.';
      throw new Error(message);
    }

    const data = await res.json();

    if (data.requiresEmailVerification) {
      persistAuth({
        ...defaultAuthState,
        email,
        requiresEmailVerification: true,
      });
      return { mfaRequired: false, requiresEmailVerification: true };
    }

    if (data.mfaRequired) {
      persistAuth({
        ...defaultAuthState,
        mfaRequired: true,
        challengeId: data.challengeId,
        email,
      });
      return { mfaRequired: true };
    }

    const permissions = await fetchPermissions(data.accessToken, data.type || null);

    persistAuth({
      ...defaultAuthState,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      email,
      type: data.type || null,
      permissions: permissions ?? null,
    });

    return { mfaRequired: false };
  }, [fetchPermissions, persistAuth]);

  const completeMfa = React.useCallback(async (code: string) => {
    const res = await fetch(`${API_BASE}/auth/mfa/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authAudienceHeaders(),
      },
      body: JSON.stringify({
        challengeId: auth.challengeId,
        code,
      }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const message = (err as { message?: string }).message
        || 'Invalid code.';
      throw new Error(message);
    }

    const data = await res.json();
    const permissions = await fetchPermissions(data.accessToken, data.type || null);
    persistAuth({
      ...defaultAuthState,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      email: auth.email,
      type: data.type || null,
      permissions: permissions ?? null,
    });
  }, [auth.challengeId, auth.email, fetchPermissions, persistAuth]);

  const resendMfaCode = React.useCallback(async () => {
    if (!auth.challengeId) {
      throw new Error('No active sign-in challenge.');
    }
    const res = await fetch(`${API_BASE}/auth/mfa/resend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ challengeId: auth.challengeId }),
    });
    const data = (await res.json().catch(() => ({}))) as {
      message?: string;
      retryAfterSeconds?: number;
    };
    if (!res.ok) {
      const err = new Error(
        typeof data.message === 'string' && data.message.trim()
          ? data.message
          : 'Unable to resend code.',
      );
      if (typeof data.retryAfterSeconds === 'number') {
        (err as Error & { retryAfterSeconds?: number }).retryAfterSeconds =
          data.retryAfterSeconds;
      }
      throw err;
    }
  }, [auth.challengeId]);

  const cancelMfaFlow = React.useCallback(() => {
    persistAuth({ ...defaultAuthState });
    try {
      window.localStorage.removeItem(LAST_ACTIVITY_KEY);
    } catch {
      // ignore
    }
  }, [persistAuth]);

  const requestAgentLoginCode = React.useCallback(async (email: string) => {
    const normalized = String(email || '').trim().toLowerCase();
    if (!normalized) {
      throw new Error('Please enter your email.');
    }
    const res = await fetch(`${API_BASE}/auth/agent/request-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: normalized }),
    });
    const data = (await res.json().catch(() => ({}))) as {
      message?: string;
      challengeId?: string;
    };
    if (!res.ok || !data.challengeId) {
      throw new Error(
        typeof data.message === 'string' && data.message.trim()
          ? data.message
          : 'We could not send your sign-in code. Please contact your admin.',
      );
    }
    persistAuth({
      ...defaultAuthState,
      mfaRequired: true,
      challengeId: data.challengeId,
      email: normalized,
    });
  }, [persistAuth]);

  const logout = React.useCallback(() => {
    // Best-effort server logout (revokes the server-side session).
    // We still clear local state even if the request fails.
    try {
      const accessToken = auth.accessToken;
      if (accessToken) {
        void fetch(`${API_BASE}/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}` },
        });
      }
    } catch {
      // ignore
    }
    persistAuth(defaultAuthState);
    clearPersistedAuth();
    try {
      window.localStorage.removeItem(LAST_ACTIVITY_KEY);
    } catch {
      // ignore
    }
  }, [auth.accessToken, persistAuth]);

  // Idle session timeout: logout after 30 min of inactivity.
  React.useEffect(() => {
    if (!isAuthenticated) return;
    const idleTimeoutMs = auth.type === 'CUSTOMER'
      ? CUSTOMER_IDLE_TIMEOUT_MS
      : STAFF_IDLE_TIMEOUT_MS;

    // Initialize activity timestamp on login.
    touchActivity();

    let raf = 0;
    const onActivity = () => {
      // Throttle frequent events.
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        touchActivity();
      });
    };

    const events: Array<keyof WindowEventMap> = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'pointerdown',
    ];

    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

    const interval = window.setInterval(() => {
      let last = Date.now();
      try {
        const raw = window.localStorage.getItem(LAST_ACTIVITY_KEY);
        if (raw) last = Number(raw) || last;
      } catch {
        // ignore
      }

      if (Date.now() - last >= idleTimeoutMs) {
        logout();
      }
    }, 15_000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity as any));
      window.clearInterval(interval);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [auth.type, isAuthenticated, logout, touchActivity]);

  const authorizedFetch = React.useCallback(async (
    input: RequestInfo | URL,
    init: RequestInit = {},
  ): Promise<Response> => {
    const unauthorizedResponse = (message: string) =>
      new Response(JSON.stringify({ message }), {
        status: 401,
        headers: { 'Content-Type': 'application/json' },
      });

    if (!auth.accessToken) {
      logout();
      return unauthorizedResponse('Not authenticated.');
    }

    const doFetch = async (token: string) => {
      const headers = new Headers(init.headers || {});
      if (!headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      // Keep workspace API calls scoped to the selected department scope.
      if (!headers.has('x-department-slug')) {
        const selectedScope = String(
          window.localStorage.getItem('workspace_department_scope') || '',
        )
          .trim()
          .toLowerCase();
        const departmentSlug = selectedScope && selectedScope !== '__all__'
          ? selectedScope
          : '';
        if (departmentSlug) {
          headers.set('x-department-slug', departmentSlug);
        }
      }
      return fetch(input, {
        ...init,
        headers,
      });
    };

    touchActivity();
    let response = await doFetch(auth.accessToken);

    // If access token expired, try refresh once then retry the request.
    if (response.status === 401 && auth.refreshToken) {
      const refreshRes = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: auth.refreshToken }),
      });

      if (refreshRes.ok) {
        const data = await refreshRes.json();
        const permissions = await fetchPermissions(data.accessToken, data.type || auth.type || null);
        persistAuth({
          ...auth,
          accessToken: data.accessToken,
          refreshToken: data.refreshToken ?? auth.refreshToken,
          type: data.type || auth.type,
          permissions: permissions ?? auth.permissions ?? null,
        });
        touchActivity();
        response = await doFetch(data.accessToken);
      }
    }

    if (response.status === 401) {
      logout();
      const resume = getReturnPathForAuthRedirect();
      const isStaffSession =
        auth.type === 'ADMIN'
        || auth.type === 'EMPLOYEE'
        || auth.type === 'SHIPPING_AGENT';
      const loginUrl = isStaffSession
        ? buildStaffLoginUrl(resume)
        : buildLoginUrl(resume);
      window.location.replace(loginUrl);
      return unauthorizedResponse('Session expired.');
    }

    return response;
  }, [auth, fetchPermissions, logout, persistAuth, touchActivity]);

  const refreshPermissions = React.useCallback(async () => {
    if (!auth.accessToken) return;
    if (auth.type === 'CUSTOMER') return;
    try {
      const permissions = await fetchPermissions(auth.accessToken, auth.type);
      if (!permissions) return;

      const prev = auth.permissions ?? null;
      const prevKey = Array.isArray(prev) ? prev.join('\u0000') : '';
      const nextKey = permissions.join('\u0000');
      if (prevKey === nextKey) return;

      persistAuth({
        ...auth,
        permissions,
      });
    } catch {
      // ignore; best-effort refresh
    }
  }, [auth, fetchPermissions, persistAuth]);

  // Keep permissions fresh while user stays logged in (role changes should reflect without logout).
  React.useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;
    const safeRefresh = () => {
      if (cancelled) return;
      void refreshPermissions();
    };

    // Refresh immediately on mount/login.
    safeRefresh();

    // Refresh when tab becomes active again.
    const onFocus = () => safeRefresh();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') safeRefresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    // Also refresh periodically (near-immediate updates without needing a reload).
    const interval = window.setInterval(safeRefresh, 15_000);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(interval);
    };
  }, [isAuthenticated, refreshPermissions]);

  const fetchPreferences = React.useCallback(async () => {
    if (!auth.accessToken) return null;
    const res = await authorizedFetch(`${API_BASE}/auth/preferences`);
    if (!res.ok) return null;
    // Some backends may reply 204 No Content for "no preferences yet".
    const text = await res.text();
    if (!text.trim()) return null;
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }, [auth.accessToken, authorizedFetch]);

  const updatePreferences = React.useCallback(async (partial: {
    language?: string;
    currency?: string;
  }) => {
    if (!auth.accessToken) return;
    await authorizedFetch(`${API_BASE}/auth/preferences`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(partial),
    });
  }, [auth.accessToken, authorizedFetch]);

  const loginWithPasskey = React.useCallback(async (email: string) => {
    const optionsRes = await fetch(`${API_BASE}/auth/passkeys/login/options`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authAudienceHeaders() },
      body: JSON.stringify({ email }),
    });
    if (!optionsRes.ok) {
      const err = await optionsRes.json().catch(() => ({}));
      throw new Error((err as { message?: string }).message || 'Passkey login is unavailable.');
    }
    const payload = await optionsRes.json();
    const credential = await startAuthentication({
      optionsJSON: payload.options,
    });
    const verifyRes = await fetch(`${API_BASE}/auth/passkeys/login/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authAudienceHeaders() },
      body: JSON.stringify({
        challengeToken: payload.challengeToken,
        credential,
      }),
    });
    if (!verifyRes.ok) {
      const err = await verifyRes.json().catch(() => ({}));
      throw new Error((err as { message?: string }).message || 'Passkey verification failed.');
    }
    const data = await verifyRes.json();
    const permissions = await fetchPermissions(data.accessToken, data.type || null);
    persistAuth({
      ...defaultAuthState,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      email,
      type: data.type || null,
      permissions: permissions ?? null,
    });
  }, [fetchPermissions, persistAuth]);

  const registerCurrentDevicePasskey = React.useCallback(async (nickname?: string) => {
    if (!auth.accessToken) {
      throw new Error('You must be signed in first.');
    }
    const optionsRes = await authorizedFetch(`${API_BASE}/auth/passkeys/register/options`, {
      method: 'POST',
    });
    if (!optionsRes.ok) {
      const err = await optionsRes.json().catch(() => ({}));
      throw new Error((err as { message?: string }).message || 'Unable to begin passkey setup.');
    }
    const payload = await optionsRes.json();
    const credential = await startRegistration({ optionsJSON: payload.options });
    const verifyRes = await authorizedFetch(`${API_BASE}/auth/passkeys/register/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        challengeToken: payload.challengeToken,
        credential,
        nickname,
      }),
    });
    if (!verifyRes.ok) {
      const err = await verifyRes.json().catch(() => ({}));
      throw new Error((err as { message?: string }).message || 'Unable to save this passkey.');
    }
  }, [auth.accessToken, authorizedFetch]);

  const regenerateRecoveryCodes = React.useCallback(async (currentPassword: string) => {
    const run = async (challenge?: { challengeToken: string; credential: Record<string, unknown> }) =>
      authorizedFetch(`${API_BASE}/auth/recovery-codes/regenerate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentPassword,
          challengeToken: challenge?.challengeToken,
          credential: challenge?.credential,
        }),
      });
    let res = await run();
    if (res.status === 401 && auth.accessToken) {
      const optRes = await authorizedFetch(`${API_BASE}/auth/passkeys/reauth/options`, { method: 'POST' });
      if (optRes.ok) {
        const opt = await optRes.json();
        const credential = await startAuthentication({ optionsJSON: opt.options });
        res = await run({
          challengeToken: opt.challengeToken,
          credential: credential as unknown as Record<string, unknown>,
        });
      }
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error((err as { message?: string }).message || 'Unable to create recovery codes.');
    }
    const data = await res.json();
    return Array.isArray(data.codes) ? data.codes : [];
  }, [auth.accessToken, authorizedFetch]);

  const recoverAccountWithCode = React.useCallback(async (payload: {
    email: string;
    code: string;
    newEmail?: string;
    newPassword: string;
  }) => {
    const optionsRes = await fetch(`${API_BASE}/auth/recover/options`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: payload.email }),
    });
    let passkeyChallengeToken: string | undefined;
    let passkeyCredential: Record<string, unknown> | undefined;
    if (optionsRes.ok) {
      const options = await optionsRes.json();
      if (options.requiresPasskey && options.challengeToken && options.options) {
        const credential = await startAuthentication({ optionsJSON: options.options });
        passkeyChallengeToken = options.challengeToken;
        passkeyCredential = credential as unknown as Record<string, unknown>;
      }
    }
    const res = await fetch(`${API_BASE}/auth/recover-account`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...payload,
        passkeyChallengeToken,
        passkeyCredential,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error((err as { message?: string }).message || 'Account recovery failed.');
    }
  }, []);

  const listPasskeys = React.useCallback(async () => {
    const res = await authorizedFetch(`${API_BASE}/auth/passkeys`);
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.passkeys) ? data.passkeys : [];
  }, [authorizedFetch]);

  const renamePasskey = React.useCallback(async (id: string, nickname?: string) => {
    const res = await authorizedFetch(`${API_BASE}/auth/passkeys/${id}/rename`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ nickname }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error((err as { message?: string }).message || 'Unable to rename passkey.');
    }
  }, [authorizedFetch]);

  const deletePasskey = React.useCallback(async (id: string) => {
    const run = async (challenge?: { challengeToken: string; credential: Record<string, unknown> }) =>
      authorizedFetch(`${API_BASE}/auth/passkeys/${id}/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          challengeToken: challenge?.challengeToken,
          credential: challenge?.credential,
        }),
      });
    let res = await run();
    if (res.status === 401 && auth.accessToken) {
      const optRes = await authorizedFetch(`${API_BASE}/auth/passkeys/reauth/options`, { method: 'POST' });
      if (optRes.ok) {
        const opt = await optRes.json();
        const credential = await startAuthentication({ optionsJSON: opt.options });
        res = await run({
          challengeToken: opt.challengeToken,
          credential: credential as unknown as Record<string, unknown>,
        });
      }
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error((err as { message?: string }).message || 'Unable to delete passkey.');
    }
  }, [auth.accessToken, authorizedFetch]);

  const consumeAuthResult = React.useCallback(async (payload: {
    accessToken: string;
    refreshToken?: string | null;
    type?: string | null;
    email?: string | null;
  }) => {
    const permissions = await fetchPermissions(
      payload.accessToken,
      payload.type || null,
    );
    persistAuth({
      ...defaultAuthState,
      accessToken: payload.accessToken,
      refreshToken: payload.refreshToken ?? null,
      email: String(payload.email || ''),
      type: payload.type || null,
      permissions: permissions ?? null,
    });
  }, [fetchPermissions, persistAuth]);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      auth,
      isAuthenticated,
      register,
      login,
      completeMfa,
      resendMfaCode,
      cancelMfaFlow,
      requestAgentLoginCode,
      logout,
      authorizedFetch,
      refreshPermissions,
      fetchPreferences,
      updatePreferences,
      loginWithPasskey,
      registerCurrentDevicePasskey,
      regenerateRecoveryCodes,
      recoverAccountWithCode,
      listPasskeys,
      renamePasskey,
      deletePasskey,
      consumeAuthResult,
    }),
    [
      auth,
      isAuthenticated,
      register,
      login,
      completeMfa,
      resendMfaCode,
      cancelMfaFlow,
      requestAgentLoginCode,
      logout,
      authorizedFetch,
      refreshPermissions,
      fetchPreferences,
      updatePreferences,
      loginWithPasskey,
      registerCurrentDevicePasskey,
      regenerateRecoveryCodes,
      recoverAccountWithCode,
      listPasskeys,
      renamePasskey,
      deletePasskey,
      consumeAuthResult,
    ],
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  return React.useContext(AuthContext);
}

